import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { dublinDateString, formatCivilDate } from '../../utils/date';
import ParentLeftChildBanner from '../../components/parent/ParentLeftChildBanner';
import {
  useListMyChildMedicationsQuery,
  useConsentMedicationMutation,
  useRevokeMedicationMutation,
  useEndMedicationMutation,
} from '../../store/api/localApi';
import {
  MedicationRequirementStatus,
  MedicationRequirementType,
  MedicationRoute,
  ParentSafeMedication,
} from '../../types';
import MedicationForm from '../../components/medications/MedicationForm';
import MedicationLog from '../../components/medications/MedicationLog';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<MedicationRequirementStatus, string> = {
  [MedicationRequirementStatus.PENDING_CONSENT]: 'Awaiting your consent',
  [MedicationRequirementStatus.ACTIVE]: 'Active',
  [MedicationRequirementStatus.ENDED]: 'Ended',
  [MedicationRequirementStatus.REVOKED]: 'Revoked',
  [MedicationRequirementStatus.EXPIRED_MEDICATION]: 'Expired',
};

const STATUS_CLASSES: Record<MedicationRequirementStatus, string> = {
  [MedicationRequirementStatus.PENDING_CONSENT]: 'bg-amber-100 text-amber-800',
  [MedicationRequirementStatus.ACTIVE]: 'bg-green-100 text-green-800',
  [MedicationRequirementStatus.ENDED]: 'bg-gray-100 text-gray-700',
  [MedicationRequirementStatus.REVOKED]: 'bg-red-100 text-red-800',
  [MedicationRequirementStatus.EXPIRED_MEDICATION]: 'bg-red-100 text-red-800',
};

const ROUTE_LABELS: Record<MedicationRoute, string> = {
  [MedicationRoute.ORAL]: 'By mouth',
  [MedicationRoute.INHALED]: 'Inhaled',
  [MedicationRoute.TOPICAL]: 'Applied to skin',
  [MedicationRoute.INJECTION]: 'Injection',
  [MedicationRoute.OTHER]: 'Other',
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Terminal statuses: nothing further can be consented, revoked, or ended. */
const TERMINAL_STATUSES: MedicationRequirementStatus[] = [
  MedicationRequirementStatus.ENDED,
  MedicationRequirementStatus.REVOKED,
];

function formatDate(iso: string): string {
  // startDate/endDate are plain YYYY-MM-DD — formatCivilDate is immune to
  // the UTC-midnight day-shift, so the date can't move west of Greenwich.
  return formatCivilDate(iso) || iso;
}

/**
 * /parent/:studentId/medications — the family view of one child's medication
 * requirements and their administration log, and the place a parent adds a
 * requirement, consents to one, revokes consent, or marks a course finished.
 *
 * Data comes from the parent-safe allowlist projection
 * (`useListMyChildMedicationsQuery`), never the staff endpoint — the family
 * never receives attachments or tenancy plumbing.
 *
 * The four actions are deliberately kept distinct, matching the server:
 *   - CONSENT is the guardian's own act, and the ONLY thing that authorises
 *     staff to give the medication. Adding a requirement is not consent, and
 *     neither is editing one.
 *   - REVOKE withdraws consent and takes effect immediately.
 *   - END records that a course finished. It is NOT revocation and the two
 *     are never collapsed — an inspector may need to tell them apart.
 * Nothing is ever deleted; a revoked or ended requirement stays visible as the
 * evidence of what was decided and when.
 */
const ParentChildMedications: React.FC = () => {
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (!activeStudent) {
    return <Navigate to={parentHomePath ?? '/parent'} replace />;
  }

  return (
    <MedicationsView
      studentId={activeStudent.id}
      locationId={activeStudent.locationId}
      childFirstName={activeStudent.firstName}
      childDisplayName={activeStudent.displayName}
      locationName={activeStudent.locationName}
      // Task 17: a LEFT child's medication log stays fully readable, but every
      // parent write (add / consent / revoke / end) is gone. The ability layer
      // enforces it; this flag just hides the controls and shows the banner.
      readOnly={activeStudent.status === 'LEFT'}
    />
  );
};

const MedicationsView: React.FC<{
  studentId: string;
  locationId: string;
  childFirstName?: string;
  childDisplayName: string;
  locationName: string;
  readOnly: boolean;
}> = ({ studentId, locationId, childFirstName, childDisplayName, locationName, readOnly }) => {
  const { data, isLoading, error } = useListMyChildMedicationsQuery({ childId: studentId });
  const [showForm, setShowForm] = useState(false);
  const requirements = data?.requirements ?? [];
  // Once per render, not per card — Intl.DateTimeFormat construction is the
  // expensive part of a Dublin-date read.
  const today = dublinDateString();

  return (
    <div className="py-6" data-testid="parent-medications-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <div>
          <Link
            to={`/parent/${studentId}`}
            data-testid="parent-medications-back"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to {childFirstName || 'profile'}
          </Link>
          <div className="mt-2 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Medication</h1>
              <p className="text-sm text-gray-500">For {childFirstName || 'this child'}</p>
            </div>
            {!showForm && !readOnly && (
              <button
                type="button"
                data-testid="medication-add-button"
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add medication
              </button>
            )}
          </div>
        </div>

        {readOnly && (
          <ParentLeftChildBanner childName={childDisplayName} locationName={locationName} />
        )}

        {showForm && !readOnly && (
          <MedicationForm
            studentId={studentId}
            locationId={locationId}
            onCancel={() => setShowForm(false)}
            onCreated={() => setShowForm(false)}
          />
        )}

        {isLoading ? (
          <div
            className="py-12 text-center text-sm text-gray-500"
            data-testid="medication-list-loading"
          >
            Loading medication…
          </div>
        ) : error ? (
          <div className="py-12 text-center" data-testid="medication-list-error">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load medication</h3>
            <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
          </div>
        ) : requirements.length === 0 ? (
          <div
            className="bg-white rounded-lg shadow px-4 py-8 text-center text-sm text-gray-500"
            data-testid="medication-list-empty"
          >
            No medication recorded for {childFirstName || 'this child'}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="medication-list">
            {requirements.map((requirement) => (
              <MedicationCard
                today={today}
                key={requirement.requirementId}
                requirement={requirement}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MedicationCard: React.FC<{
  requirement: ParentSafeMedication;
  readOnly: boolean;
  /** Dublin civil date, computed once by the page — not per card. */
  today: string;
}> = ({ requirement, readOnly, today }) => {
  const { showSuccess, showError } = useToast();
  const [consentMedication, { isLoading: isConsenting }] = useConsentMedicationMutation();
  const [revokeMedication, { isLoading: isRevoking }] = useRevokeMedicationMutation();
  const [endMedication, { isLoading: isEnding }] = useEndMedicationMutation();
  const [showLog, setShowLog] = useState(false);

  const { requirementId, status } = requirement;
  const isTerminal = TERMINAL_STATUSES.includes(status);
  const isBusy = isConsenting || isRevoking || isEnding;

  const runAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string
  ) => {
    try {
      await action();
      showSuccess(successMessage);
    } catch (err: unknown) {
      const data =
        typeof err === 'object' && err !== null && 'data' in err
          ? (err as { data?: { error?: string; message?: string } }).data
          : undefined;
      showError(data?.error || data?.message || failureMessage);
    }
  };

  return (
    <section
      data-testid={`medication-card-${requirementId}`}
      className="bg-white rounded-lg shadow p-4 sm:p-5 space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">{requirement.medicationName}</h2>
          <p className="text-sm text-gray-600">
            {requirement.dosage} · {ROUTE_LABELS[requirement.route]}
          </p>
        </div>
        <span
          data-testid="medication-card-status"
          className={`inline-flex flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CLASSES[status]}`}
        >
          {STATUS_LABELS[status]}
        </span>
      </div>

      {requirement.requirementType === MedicationRequirementType.EMERGENCY_ONLY ? (
        <p className="text-sm text-gray-700">
          <span className="font-medium">Emergency only.</span>{' '}
          {requirement.emergencyInstructions || 'No instructions recorded.'}
        </p>
      ) : (
        requirement.schedule && (
          <p className="text-sm text-gray-700" data-testid="medication-card-schedule">
            {requirement.schedule.timesOfDay.join(', ')} on{' '}
            {requirement.schedule.daysOfWeek.map((d) => DAY_LABELS[d] ?? d).join(', ')}
          </p>
        )
      )}

      <dl className="text-xs text-gray-500 space-y-0.5">
        <div>
          <dt className="inline">From </dt>
          <dd className="inline">{formatDate(requirement.startDate)}</dd>
          {requirement.endDate && (
            <>
              <dt className="inline"> until </dt>
              <dd className="inline">{formatDate(requirement.endDate)}</dd>
            </>
          )}
        </div>
        {requirement.prescriber && (
          <div>
            <dt className="inline">Prescribed by </dt>
            <dd className="inline">{requirement.prescriber}</dd>
          </div>
        )}
        {requirement.medicationExpiryDate &&
          (requirement.medicationExpiryDate < today ? (
            /* Loud even while the status still reads Active: the daily sweep
               is what flips the status, and the medicine in the cabinet is
               already unusable in the window before it runs. */
            <div
              data-testid={`medication-expired-warning-${requirementId}`}
              className="mt-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 font-medium text-red-800"
            >
              Medication expired {formatDate(requirement.medicationExpiryDate)} — please supply a
              replacement.
            </div>
          ) : (
            <div>
              <dt className="inline">Medication expires </dt>
              <dd className="inline">{formatDate(requirement.medicationExpiryDate)}</dd>
            </div>
          ))}
      </dl>

      {requirement.specialInstructions && (
        <p className="text-sm text-gray-700">{requirement.specialInstructions}</p>
      )}

      {/* The child's attending days changed after this schedule was agreed, so
          the schedule may no longer line up with when they are actually here. */}
      {requirement.needsScheduleReview && (
        <div
          data-testid="medication-needs-review-flag"
          className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
        >
          <ExclamationTriangleIcon aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
          <span>
            Your child&apos;s days at the service have changed since this schedule was agreed.
            Please check the days below still suit.
          </span>
        </div>
      )}

      {status === MedicationRequirementStatus.REVOKED && requirement.consent.revokedAt && (
        <p className="text-xs text-red-700" data-testid="medication-card-revoked-detail">
          Consent withdrawn on {formatDate(requirement.consent.revokedAt.slice(0, 10))}
          {requirement.consent.revokedReason ? ` — ${requirement.consent.revokedReason}` : ''}.
        </p>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {/* Task 17: consent / revoke / end are parent WRITES — gone once the
            child has LEFT. The administration log toggle below stays (a read). */}
        {!readOnly && status === MedicationRequirementStatus.PENDING_CONSENT && (
          <button
            type="button"
            data-testid="medication-consent-button"
            disabled={isBusy}
            onClick={() =>
              runAction(
                () => consentMedication(requirementId).unwrap(),
                'Consent recorded.',
                'Could not record consent.'
              )
            }
            className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            Give consent
          </button>
        )}

        {!readOnly && !isTerminal && (
          <button
            type="button"
            data-testid="medication-revoke-button"
            disabled={isBusy}
            onClick={() =>
              runAction(
                () => revokeMedication({ medicationId: requirementId }).unwrap(),
                'Consent withdrawn.',
                'Could not withdraw consent.'
              )
            }
            className="px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Withdraw consent
          </button>
        )}

        {!readOnly && !isTerminal && (
          <button
            type="button"
            data-testid="medication-end-button"
            disabled={isBusy}
            onClick={() =>
              runAction(
                () => endMedication({ medicationId: requirementId }).unwrap(),
                'Marked as finished.',
                'Could not mark this as finished.'
              )
            }
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Course finished
          </button>
        )}

        <button
          type="button"
          data-testid="medication-log-toggle"
          onClick={() => setShowLog((v) => !v)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          {showLog ? 'Hide log' : `Administration log (${requirement.entries.length})`}
        </button>
      </div>

      {showLog && (
        <div className="border-t border-gray-100 pt-3">
          <MedicationLog entries={requirement.entries} />
        </div>
      )}
    </section>
  );
};

export default ParentChildMedications;
